import type { SerializedStyles } from '@emotion/react';
import { tokens } from '@env0/ui-theme';
import { env0Themes, type Env0Theme as GeneratedEnv0Theme } from '@env0/ui-theme/env0';
import { showNewDesign } from 'constants/config';
import {
  ANTD_BREAKPOINTS,
  LEGACY_LAYOUT,
  sharedPrimitives,
  type Breakpoints,
  type DesignPrimitives,
  type LayoutDimensions
} from './design-primitives.constants';
import { legacyThemes } from './themes.constants.legacy';

// Re-exported so the theme module stays the one place callers look for theme-adjacent constants.
export { ANTD_BREAKPOINTS, LEGACY_LAYOUT };
export type { Breakpoints, LayoutDimensions };

export const TOP_BAR_HEIGHT = LEGACY_LAYOUT.topBarHeight;
export const NAVBAR_WIDTH = LEGACY_LAYOUT.navbarWidth;
export const NAVBAR_COLLAPSED_WIDTH = LEGACY_LAYOUT.navbarCollapsedWidth;
export const NAVBAR_TRANSITION_DURATION = LEGACY_LAYOUT.navbarTransitionDuration;
export const BREADCRUMB_BAR_HEIGHT = LEGACY_LAYOUT.breadcrumbBarHeight;

/**
 * The generated bridge is rebuilt from Figma, so the switch is added here instead. Styled components
 * branch on it through `legacyOnly` / `newDesignOnly` below, and APO-484 deletes the key, the helpers
 * and every call site after the flip.
 */
export type Env0Theme = GeneratedEnv0Theme &
  DesignPrimitives & {
    isNewDesign: boolean;
    breakpoints: Breakpoints;
    layout: LayoutDimensions;
    loaderSpinnerTrack: string;
    loaderSpinnerHead: string;
    /**
     * kbar's scrim, the one colour the palette cannot take from a `var()`: `kbar-wrapper.tsx` was on the
     * raw-colour lint allowlist because of it, and a `legacyOnly` block would still hold the literal.
     * Same reason loaderSpinner* are here. APO-484 deletes all three.
     */
    kbarScrim: string;
  };

/**
 * The two shell dimensions where the Figma frames and Figma's own `Layout` collection disagree. The
 * collection is what `tokens.layout` mirrors and it still carries the legacy numbers, so per D7.1 the
 * frames win:
 *
 * - Breadcrumb nav (225:2043) is 38px, not 35. `inner-layout.tsx` and `navbar-header.tsx` both read it,
 *   which keeps the rail header level with the strip (APO-471).
 * - Navigation / Top (225:2025) is 60px, not 50. Nothing reads this any more: the new top bar builds its
 *   height from padding, so only the legacy branch reads `topBarHeight` (APO-468).
 */
const NEW_DESIGN_LAYOUT = { ...tokens.layout, topBarHeight: '60px', breadcrumbBarHeight: '38px' };

/**
 * The loader's Suspense-fallback spinner had its two colours inlined as hex, which is what kept
 * `loader.tsx` on the lint allowlist. They live here instead so the file could come off it (APO-466).
 *
 * The new design reads those tokens directly and never these keys. APO-484 deletes both.
 */
const newDesignPrimitives = {
  ...sharedPrimitives,
  breakpoints: ANTD_BREAKPOINTS,
  layout: NEW_DESIGN_LAYOUT,
  loaderSpinnerTrack: tokens.colors.neutral['600'],
  loaderSpinnerHead: tokens.colors.primary['500'],
  kbarScrim: tokens.theme.light.surface.main['modal-backdrop'],
  isNewDesign: true
} as const;

type BranchStyles = SerializedStyles | ((theme: Env0Theme) => SerializedStyles);

const resolve = (styles: BranchStyles, theme: Env0Theme) => (typeof styles === 'function' ? styles(theme) : styles);

/** The switch-off half of a wrapper's single structural branch (D2). APO-484 deletes every call site. */
export const legacyOnly =
  (styles: BranchStyles) =>
  ({ theme }: { theme: Env0Theme }) =>
    theme.isNewDesign ? undefined : resolve(styles, theme);

/** The switch-on half. Read DS tokens here — never legacy Env0Theme key names (D0). */
export const newDesignOnly =
  (styles: BranchStyles) =>
  ({ theme }: { theme: Env0Theme }) =>
    theme.isNewDesign ? resolve(styles, theme) : undefined;

export const newDesignThemes = {
  light: { ...env0Themes.light, ...newDesignPrimitives },
  dark: { ...env0Themes.dark, ...newDesignPrimitives }
} satisfies Record<'light' | 'dark', Env0Theme>;

// Some generated tokens still carry wrong values, so the legacy themes stay the default.
export const selectThemes = (isNewDesign: boolean) => (isNewDesign ? newDesignThemes : legacyThemes);

const themes = selectThemes(showNewDesign());

export const darkTheme = themes.dark;

declare module '@emotion/react' {
  export interface Theme extends Env0Theme {}
}

export default themes.light;
